<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import KaptionAssistantSelector from './KaptionAssistantSelector.vue';
import ScopeSelector from './ScopeSelector.vue';
import TokenCreatedModal from './TokenCreatedModal.vue';

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    KaptionAssistantSelector,
    ScopeSelector,
    TokenCreatedModal,
  },
  data() {
    return {
      name: '',
      kaptionAssistantId: null,
      selectedScopes: [],
      expiresIn: '90_days',
      showTokenModal: false,
      createdToken: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'kaptionAccessTokens/getUIFlags',
      kaptionAssistants: 'kaptionAccessTokens/getKaptionAssistants',
      scopes: 'kaptionAccessTokens/getScopes',
    }),
    expirationOptions() {
      return [
        {
          value: '7_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_7'),
        },
        {
          value: '30_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_30'),
        },
        {
          value: '60_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_60'),
        },
        {
          value: '90_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_90'),
        },
        {
          value: '1_year',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.YEAR_1'),
        },
        {
          value: 'never',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.NEVER'),
        },
      ];
    },
    isFormValid() {
      return (
        this.name.trim() !== '' &&
        this.kaptionAssistantId !== null &&
        this.selectedScopes.length > 0
      );
    },
  },
  mounted() {
    this.$store.dispatch('kaptionAccessTokens/fetchKaptionAssistants');
    this.$store.dispatch('kaptionAccessTokens/fetchScopes');
  },
  methods: {
    onAssistantSelect(assistant) {
      this.kaptionAssistantId = assistant?.id || null;
    },
    onScopesChange(scopes) {
      this.selectedScopes = scopes;
    },
    navigateBack() {
      this.$router.push({
        name: 'settings_kaption_access_tokens',
      });
    },
    hideTokenModal() {
      this.showTokenModal = false;
      this.navigateBack();
    },
    async createToken() {
      if (!this.isFormValid) return;

      try {
        const response = await this.$store.dispatch(
          'kaptionAccessTokens/create',
          {
            kaption_access_token: {
              name: this.name.trim(),
              kaption_assistant_id: this.kaptionAssistantId,
              scopes: this.selectedScopes,
              expires_in: this.expiresIn,
            },
          }
        );

        this.createdToken = response;
        this.showTokenModal = true;

        useAlert(this.$t('KAPTION_ACCESS_TOKENS.ADD.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          this.$t('KAPTION_ACCESS_TOKENS.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingAssistants || uiFlags.fetchingScopes"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_ACCESS_TOKENS.ADD.TITLE')"
        :description="$t('KAPTION_ACCESS_TOKENS.ADD.DESCRIPTION')"
        :back-button-label="$t('KAPTION_ACCESS_TOKENS.HEADER')"
        show-back-button
        @back="navigateBack"
      />
    </template>
    <template #body>
      <form class="max-w-2xl space-y-6" @submit.prevent="createToken">
        <!-- Token Name -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="name"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            :placeholder="$t('KAPTION_ACCESS_TOKENS.FORM.NAME.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.NAME.HELP') }}
          </p>
        </div>

        <!-- Kaption Assistant Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.LABEL') }}
          </label>
          <KaptionAssistantSelector
            :assistants="kaptionAssistants"
            :selected-id="kaptionAssistantId"
            @select="onAssistantSelect"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.HELP') }}
          </p>
        </div>

        <!-- Scope Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.LABEL') }}
          </label>
          <ScopeSelector
            :scopes="scopes"
            :selected-scopes="selectedScopes"
            @change="onScopesChange"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.HELP') }}
          </p>
        </div>

        <!-- Expiration -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.LABEL') }}
          </label>
          <select
            v-model="expiresIn"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          >
            <option
              v-for="option in expirationOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <NextButton
            blue
            :label="$t('KAPTION_ACCESS_TOKENS.FORM.SUBMIT')"
            :disabled="!isFormValid"
            :is-loading="uiFlags.creatingItem"
            type="submit"
          />
          <NextButton
            slate
            faded
            :label="$t('KAPTION_ACCESS_TOKENS.FORM.CANCEL')"
            @click="navigateBack"
          />
        </div>
      </form>
    </template>

    <TokenCreatedModal
      v-if="showTokenModal"
      :token="createdToken"
      @close="hideTokenModal"
    />
  </SettingsLayout>
</template>
